import { z } from "zod";

import { isValidUsername, normalizeUsername } from "@pharmacy/domain";

import { staffPasswordSchema } from "../auth/login";

/**
 * Staff, created by an admin inside an existing pharmacy.
 *
 * No public entry point and no phone verification — a cashier frequently has no
 * phone, and requiring one would mean being unable to hire them. The admin is
 * present when this happens; that is the authorisation.
 *
 * Only staff can be created this way. An admin is identified by a VERIFIED
 * phone number, so creating one here would produce an owner with no recovery
 * channel — locked out permanently the first time they forget their password.
 * A second owner goes through signup.
 */

export const usernameField = z
  .string()
  .trim()
  .transform((v) => normalizeUsername(v))
  .refine(
    (v) => isValidUsername(v),
    "3–30 characters, starting with a letter. Letters, numbers, dots and underscores only.",
  );

/** The nine grantable sections plus the four extra switches. */
export const staffAccessSchema = z.object({
  pos: z.boolean(),
  products: z.boolean(),
  batches: z.boolean(),
  purchases: z.boolean(),
  transfers: z.boolean(),
  customers: z.boolean(),
  invoices: z.boolean(),
  reports: z.boolean(),
  settings: z.boolean(),
  seeCostPrice: z.boolean(),
  voidOrRefundSale: z.boolean(),
  giveDiscount: z.boolean(),
  sellOnCredit: z.boolean(),
});

export const createStaffRequest = z.object({
  name: z.string().trim().min(2, "Enter their name").max(120),
  username: usernameField,
  /** Contact details only. Never a credential, never a recovery channel. */
  phone: z.string().trim().max(40).optional(),
  email: z.string().trim().email("That is not a valid email").optional(),

  /** At least one. Staff see only the branches they are assigned to. */
  branchIds: z.array(z.string().uuid()).min(1, "Assign at least one branch"),

  /** Omitted means the default: the till, and read-only products. */
  access: staffAccessSchema.partial().optional(),

  /**
   * Omitted means the system generates one and shows it once.
   *
   * Supplied means the owner chose it — usually because they are standing with
   * the new hire and want something that person will actually remember. Both
   * are legitimate; a generated password nobody can recall gets written on a
   * note by the till, which is worse than a short one they know.
   */
  password: staffPasswordSchema.optional(),
});

export const createStaffResponse = z.object({
  id: z.string().uuid(),
  name: z.string(),
  username: z.string(),
  /**
   * Shown ONCE, to the admin, to hand over.
   *
   * Never retrievable again — only resettable, which generates a new one and
   * invalidates this. A password the system can show twice is a password the
   * system knows, and this one is stored only as a hash. Echoed back even when
   * the owner supplied it, so one screen can show what to hand over either way.
   */
  password: z.string(),
  branchIds: z.array(z.string().uuid()),
});

export const updateStaffRequest = z.object({
  name: z.string().trim().min(2).max(120).optional(),
  phone: z.string().trim().max(40).nullable().optional(),
  email: z.string().trim().email().nullable().optional(),
  branchIds: z.array(z.string().uuid()).min(1).optional(),
  access: staffAccessSchema.partial().optional(),
  /** Deactivating is how somebody leaves. Nothing is ever deleted. */
  isActive: z.boolean().optional(),
});

/* ------------------------------------------------------------------ owners */

/**
 * Owners (the owner's decision, 2026-10-12). One owner is the MAIN owner —
 * whoever signed the pharmacy up, until they hand it on. Only the main owner
 * may deactivate another owner, or activate them again. Any other owner can
 * only leave by themselves. Ileyk support changes the main owner by hand in a
 * dispute. Deactivated, never deleted: their name stays on what they recorded.
 */

/** POST /users/:id/owner-active — main owner only, never themselves. */
export const setOwnerActiveRequest = z.object({ isActive: z.boolean() });

/** POST /users/:id/make-main-owner — the main owner hands the role on, with their password. */
export const makeMainOwnerRequest = z.object({ password: z.string().min(1, "Enter your password") });

/** POST /users/leave — an owner who is not the main owner leaves, with their password. */
export const leavePharmacyRequest = z.object({ password: z.string().min(1, "Enter your password") });

export type SetOwnerActiveRequest = z.infer<typeof setOwnerActiveRequest>;
export type MakeMainOwnerRequest = z.infer<typeof makeMainOwnerRequest>;
export type LeavePharmacyRequest = z.infer<typeof leavePharmacyRequest>;

export type CreateStaffRequest = z.infer<typeof createStaffRequest>;
export type CreateStaffResponse = z.infer<typeof createStaffResponse>;
export type UpdateStaffRequest = z.infer<typeof updateStaffRequest>;
